interface ScriptLoadOptions {
  priority?: 'high' | 'normal' | 'low';
  defer?: boolean;
  async?: boolean;
  timeout?: number;
}

class SmartScriptLoader {
  private loadedScripts = new Set<string>();
  private loadingPromises = new Map<string, Promise<void>>();
  private scriptCache = new Map<string, HTMLScriptElement>();

  async loadScript(
    src: string, 
    options: ScriptLoadOptions = {}
  ): Promise<void> {
    if (this.loadedScripts.has(src)) {
      return Promise.resolve();
    }

    if (this.loadingPromises.has(src)) {
      return this.loadingPromises.get(src)!;
    }

    const promise = this.loadScriptInternal(src, options);
    this.loadingPromises.set(src, promise);
    
    try {
      await promise;
      this.loadedScripts.add(src);
    } finally {
      this.loadingPromises.delete(src);
    }
  }

  private async loadScriptInternal(
    src: string, 
    options: ScriptLoadOptions
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.async = options.async ?? true;
      script.defer = options.defer ?? false;
      
      if (options.priority === 'high') {
        script.setAttribute('data-priority', 'high');
      }

      const timeout = options.timeout || 10000;
      const timeoutId = setTimeout(() => {
        reject(new Error(`Script load timeout: ${src}`));
      }, timeout);

      script.onload = () => {
        clearTimeout(timeoutId);
        this.scriptCache.set(src, script);
        resolve();
      };

      script.onerror = () => {
        clearTimeout(timeoutId);
        reject(new Error(`Failed to load script: ${src}`));
      };

      // Insert based on priority
      if (options.priority === 'high') {
        document.head.insertBefore(script, document.head.firstChild);
      } else {
        document.head.appendChild(script);
      }
    });
  }

  // Load social media scripts only when needed
  async loadSocialScript(platform: string): Promise<void> {
    const scriptMap: Record<string, string> = {
      twitter: 'https://platform.twitter.com/widgets.js',
      instagram: 'https://www.instagram.com/embed.js',
      tiktok: 'https://www.tiktok.com/embed.js',
      youtube: 'https://www.youtube.com/iframe_api',
      facebook: 'https://connect.facebook.net/en_US/sdk.js'
    };

    const scriptSrc = scriptMap[platform];
    if (!scriptSrc) return;

    await this.loadScript(scriptSrc, {
      priority: 'low',
      timeout: 5000
    });
  }

  // Preload critical scripts
  // ✅ PHASE B2: Removed Instagram preload to eliminate browser warnings
  // Scripts are lazy-loaded when needed via loadSocialScript()
  preloadCriticalScripts(): void {
    const criticalScripts = [
      'https://platform.twitter.com/widgets.js'
      // Instagram embed.js removed - lazy load when needed to avoid preload warnings
    ];

    criticalScripts.forEach(src => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'script';
      link.href = src;
      document.head.appendChild(link);
    });
  }

  // Clean up unused scripts
  cleanup(): void {
    this.scriptCache.forEach((script, src) => {
      if (!this.loadedScripts.has(src)) {
        script.remove();
        this.scriptCache.delete(src);
      }
    });
  }
}

export const smartScriptLoader = new SmartScriptLoader();
